/**
 * Procurement (Purchase) page catalog — the grantable pages for the Team &
 * Access per-member access checklist, mirroring how Spares/IMS use allowedPages.
 *
 * `key` is the stable grant token stored in User.allowedPages (prefixed `proc:`
 * so it never collides with Spares/IMS catalog keys on a multi-realm account).
 * `path` is the route the sidebar nav links to. `roles` (optional) narrows which
 * legacy procurementRoles see the page WHEN a member has no explicit grants set
 * (allowedPages == null → backward-compatible role-based visibility).
 *
 * 'Team & Access' is intentionally NOT here — it's admin-only and never granted
 * to a non-admin member.
 */
export interface ProcPage {
  key: string;
  label: string;
  path: string;
  /** Legacy role visibility used only when a member has no explicit grants. */
  roles: Array<'purchaser' | 'hod' | 'accounts' | 'logistics' | 'depot'>;
}

export const PROC_PAGES: ProcPage[] = [
  { key: 'proc:dashboard',       label: 'Dashboard',       path: '/procurement',                 roles: ['purchaser', 'hod', 'accounts', 'logistics', 'depot'] },
  { key: 'proc:create-indent',   label: 'Create Indent',   path: '/procurement/indents/new',     roles: ['purchaser'] },
  { key: 'proc:indents',         label: 'All Indents',     path: '/procurement/indents',         roles: ['purchaser', 'hod', 'accounts', 'logistics', 'depot'] },
  { key: 'proc:domestic',        label: 'Domestic Queue',  path: '/procurement/domestic',        roles: ['purchaser'] },
  { key: 'proc:import',          label: 'Import Queue',    path: '/procurement/import',          roles: ['purchaser'] },
  { key: 'proc:sales-enquiries', label: 'Sales Enquiries', path: '/procurement/sales-enquiries', roles: ['purchaser'] },
  { key: 'proc:finance',         label: 'Finance Desk',    path: '/procurement/finance',         roles: ['accounts'] },
  { key: 'proc:logistics',       label: 'Logistics Desk',  path: '/procurement/logistics',       roles: ['logistics'] },
  { key: 'proc:vessel-tracker',  label: 'Vessel Tracker',  path: '/procurement/vessel-tracker',  roles: ['purchaser', 'hod', 'accounts', 'logistics', 'depot'] },
  { key: 'proc:approvals',       label: 'Approvals',       path: '/procurement/approvals',       roles: ['hod'] },
  { key: 'proc:vendors',         label: 'Vendors',         path: '/procurement/vendors',         roles: ['purchaser'] },
  // Purchase efficiency lives in the Purchase dashboard, not the Service Desk.
  { key: 'proc:efficiency',      label: 'Efficiency',      path: '/procurement/efficiency',      roles: ['purchaser', 'hod'] },
  { key: 'proc:porta-cabin',     label: 'Porta Cabin',     path: '/procurement/porta-cabin',     roles: ['purchaser'] },
  { key: 'proc:replenishment',   label: 'IMS Replenish',   path: '/procurement/replenishment',   roles: ['purchaser'] },
  { key: 'proc:vehicle-placement', label: 'Vehicle Placement', path: '/procurement/vehicle-placement', roles: ['purchaser', 'logistics'] },
  { key: 'proc:client-orders', label: 'Client Orders', path: '/procurement/client-orders', roles: ['purchaser', 'hod', 'accounts', 'logistics', 'depot'] },
  { key: 'proc:monday-report',   label: 'Monday Report',   path: '/procurement/monday-report',   roles: ['purchaser', 'hod', 'accounts', 'logistics', 'depot'] },
  { key: 'proc:search',          label: 'Search',          path: '/procurement/search',          roles: ['purchaser', 'hod', 'accounts', 'logistics', 'depot'] },
];

export const PROC_PAGE_BY_KEY: Record<string, ProcPage> = Object.fromEntries(PROC_PAGES.map((p) => [p.key, p]));

/**
 * Should this procurement member see the page identified by `key`?
 *   • admin           → everything
 *   • allowedPages == null (legacy / unconfigured) → fall back to role visibility
 *   • allowedPages is an array → only the explicitly-granted keys
 */
export function isProcPageAllowed(
  opts: { procurementRole?: string | null; allowedPages?: string[] | null },
  page: ProcPage,
): boolean {
  if (opts.procurementRole === 'admin') return true;
  const granted = opts.allowedPages;
  if (Array.isArray(granted)) return granted.includes(page.key);
  // No explicit grants → legacy role-based visibility (backward compatible).
  const role = String(opts.procurementRole || '');
  return page.roles.includes(role as any);
}
